import { forwardRef, Fragment, useMemo } from 'react';
import { Menu, MenuItem } from 'react-bootstrap-typeahead';
import { groupBy, sortBy } from 'lodash';
import StandardTypeahead, { getOptionLabel } from './basics/StandardTypeahead';

const mapGroupOptions = (groupId, options = [], header = null) =>
  options.map((option) =>
    typeof option === 'string'
      ? { value: option, groupId, header }
      : { ...option, groupId, header },
  );

const GroupOptions = ({ options, position, renderMenuItemChildren, state }) => {
  const sortedOptions = useMemo(() => sortBy(options, state.labelKey), [
    options,
    state.labelKey,
  ]);
  return sortedOptions.map((option, i) => {
    const optionLabel = getOptionLabel(option, state.labelKey);
    return (
      <MenuItem
        disabled={option.disabled}
        key={`${position + i}-${optionLabel}`}
        option={option}
        position={position + i}
      >
        {renderMenuItemChildren(option, state)}
      </MenuItem>
    );
  });
};

const renderMenu = (newSelectionPrompt) => (results, menuProps, state) => {
  const { renderMenuItemChildren } = menuProps;
  const { text } = state;
  const groups = groupBy(results, 'groupId');
  let position = 0;
  const groupItems = Object.keys(groups)
    .sort()
    .map((groupId, i) => {
      const groupOptions = groups[groupId];
      // Can't group by header because it may affect sort, so we get first header in options (if it exists)
      const header = groupOptions.find((option) => option.header)?.header;
      const group = (
        <Fragment key={groupId}>
          {i > 0 && <Menu.Divider />}
          {header && <Menu.Header className="font-small">{header}</Menu.Header>}
          <GroupOptions
            options={groupOptions}
            position={position}
            renderMenuItemChildren={renderMenuItemChildren}
            state={state}
          />
        </Fragment>
      );
      position += groupOptions.length;
      return group;
    });

  const items = [
    // Display prompt if no input text
    ...(newSelectionPrompt && !text?.length
      ? [
          <Fragment key="new-selection-prompt">
            <div className="dropdown-header">{newSelectionPrompt}</div>
            {groupItems.length > 0 && <Menu.Divider />}
          </Fragment>,
        ]
      : []),
    ...groupItems,
  ];

  return <Menu {...menuProps}>{items}</Menu>;
};

// Renders typeahead options menu in sorted groups.
// eslint-disable-next-line react/display-name
const GroupedTypeahead = forwardRef(
  (
    {
      labelKey = 'value',
      multiple,
      /* Prompt rendered at the top when the user has not typed anything in the input */
      newSelectionPrompt,
      /* Array of option groups; each option group has an array of options and an optional header */
      optionGroups,
      options,
      ...props
    },
    ref,
  ) => {
    const optionsWithGroupIds = useMemo(
      () =>
        optionGroups
          ? // For each option group in array, add a groupId to each option in the group
            optionGroups.flatMap(
              ({ header, options: groupOptions }, groupId) => {
                // Add groupId (group index) and header (if present) to all options in the group
                return mapGroupOptions(groupId, groupOptions, header);
              },
            )
          : mapGroupOptions(0, options), // Put all options in one group
      [optionGroups, options],
    );

    return (
      <StandardTypeahead
        labelKey={labelKey}
        multiple={multiple}
        options={optionsWithGroupIds}
        paginate={false}
        ref={ref}
        renderMenu={renderMenu(newSelectionPrompt)}
        {...props}
      />
    );
  },
);

export default GroupedTypeahead;
